import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import SearchSuggestionsDropdown from './shop-search/SearchSuggestionsDropdown'
import { useLiveSearchSuggestions } from './shop-search/useLiveSearchSuggestions'
import type { SearchSuggestion } from './shop-search/types'
import { buildProductPathFromIdentity } from '../catalog/routing'

type Props = {
  placeholder: string
  ctaLabel: string
  toLocalizedPath: (path: string) => string
  className?: string
}

const ShopSearchBar = ({ placeholder, ctaLabel, toLocalizedPath, className }: Props) => {
  const inputId = useId()
  const [query, setQuery] = useState('')
  const blurTimeoutRef = useRef<number | null>(null)

  const {
    trimmedQuery,
    liveLoading,
    suggestions,
    suggestionsOpen,
    activeIndex,
    setActiveIndex,
    openSuggestions,
    closeSuggestions,
    moveSelection,
  } = useLiveSearchSuggestions(query)

  const navigateToSuggestion = (suggestion: SearchSuggestion) => {
    if (typeof window === 'undefined') return
    const productPath = buildProductPathFromIdentity({
      publicCode: suggestion.publicCode,
      slug: suggestion.slug,
    })
    window.location.assign(toLocalizedPath(productPath))
  }

  useEffect(
    () => () => {
      if (typeof window !== 'undefined' && blurTimeoutRef.current !== null) {
        window.clearTimeout(blurTimeoutRef.current)
      }
    },
    [],
  )

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!suggestions.length) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      moveSelection(1)
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      moveSelection(-1)
      return
    }
    if (event.key === 'Escape') {
      closeSuggestions()
      return
    }
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (typeof window === 'undefined') return

    const trimmed = query.trim()
    if (!trimmed) {
      window.location.assign(toLocalizedPath('/'))
      return
    }
    window.location.assign(`${toLocalizedPath('/search')}?search=${encodeURIComponent(trimmed)}`)
  }

  return (
    <div className={`relative min-w-[340px] ${className ?? ''}`}>
      <form
        onSubmit={(event) => {
          handleSubmit(event)
        }}
        className="ca-theme-transition flex min-h-12 w-full items-stretch overflow-hidden rounded-2xl border border-(--color-border-default) bg-(--color-bg-card) shadow-(--shadow-sm)"
        role="search"
        aria-label={placeholder}
      >
        <label className="sr-only" htmlFor={inputId}>
          {placeholder}
        </label>
        <span className="grid w-12 place-items-center text-(--color-text-muted)" aria-hidden>
          <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-[2]">
            <circle cx="11" cy="11" r="6" />
            <path d="M16 16l4 4" />
          </svg>
        </span>
        <input
          id={inputId}
          role="combobox"
          aria-autocomplete="list"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onFocus={() => {
            openSuggestions()
          }}
          onBlur={() => {
            if (typeof window === 'undefined') return
            blurTimeoutRef.current = window.setTimeout(() => {
              closeSuggestions()
            }, 120)
          }}
          onKeyDown={handleInputKeyDown}
          className="min-w-0 flex-1 bg-transparent pr-1 text-[15px] text-(--color-text-primary) outline-none placeholder:text-(--color-text-muted)"
          placeholder={placeholder}
          autoComplete="off"
          aria-expanded={suggestionsOpen && trimmedQuery.length >= 2}
          aria-controls={`${inputId}-results`}
          aria-activedescendant={
            activeIndex >= 0 && activeIndex < suggestions.length ? `${inputId}-item-${activeIndex}` : undefined
          }
        />
        <button
          type="submit"
          className="ca-theme-transition inline-flex min-w-[96px] items-center justify-center gap-1.5 border-l border-(--color-border-default) bg-(--color-action-primary) px-4 text-sm font-semibold text-(--color-action-primary-foreground) hover:bg-(--color-action-primary-hover) sm:min-w-[104px] disabled:cursor-not-allowed disabled:opacity-55"
        >
          <span>{ctaLabel}</span>
        </button>
      </form>

      <SearchSuggestionsDropdown
        inputId={inputId}
        suggestionsOpen={suggestionsOpen}
        liveLoading={liveLoading}
        suggestions={suggestions}
        activeIndex={activeIndex}
        trimmedQuery={trimmedQuery}
        onHoverIndex={setActiveIndex}
        onNavigateToSuggestion={navigateToSuggestion}
      />
    </div>
  )
}

export default ShopSearchBar
